import React, { useState, useEffect, useRef } from 'react';
import { CopyBlock, dracula } from 'react-code-blocks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMessageContentRendererProps {
  content: string;
}

const getLanguageLabel = (lang: string): string => {
  switch (lang.toLowerCase()) {
    case 'csharp': return 'C#';
    case 'javascript': return 'JavaScript';
    case 'typescript': return 'TypeScript';
    case 'python': return 'Python';
    case 'sql': return 'SQL';
    case 'json': return 'JSON';
    case 'java': return 'Java';
    default: return lang.toUpperCase();
  }
};

const TypingIndicator: React.FC<{ language: string }> = ({ language }) => {
  const [dots, setDots] = useState('.');
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        return prev.length >= 3 ? '.' : prev + '.';
      });
    }, 400);
    return () => {
      clearInterval(interval);
    };
  }, []);
  return <div className="typing-placeholder fade-in">✨ Generating {getLanguageLabel(language)} code{dots}</div>;
};

export const ChatMessageContentRenderer: React.FC<ChatMessageContentRendererProps> = ({ content }) => {
  const [parsedNodes, setParsedNodes] = useState<React.ReactNode[]>([]);
  const lineIndexRef = useRef(0);

  useEffect(() => {
    const lines = content.split('\n');
    const currentNodes: React.ReactNode[] = [];
    let buffer = '';
    let isInCodeBlock = false;
    let currentLanguage = 'plaintext';
    let isInExplanation = false;
    let codeBlockStartIndex = -1;

    const interval = setInterval(() => {
      if (lineIndexRef.current >= lines.length) {
        clearInterval(interval);
        return;
      }

      const line = lines[lineIndexRef.current];
      const trimmed = line.trim();

      if (trimmed.startsWith('<code-block')) {
        if (!isInCodeBlock) {
          const langMatch = trimmed.match(/language=\"(.*?)\"/);
          currentLanguage = langMatch ? langMatch[1] : 'plaintext';
          isInCodeBlock = true;
          codeBlockStartIndex = currentNodes.length;
          currentNodes.push(
            <TypingIndicator key={`placeholder-${lineIndexRef.current}`} language={currentLanguage} />
          );
          buffer = '';
        }
      } else if (trimmed === '</code-block>' || (isInCodeBlock && buffer.split('\n').length > 200)) {
        currentNodes[codeBlockStartIndex] = (
          <div
            key={`codeblock-container-${lineIndexRef.current}`}
            style={{
              overflowX: 'auto', // Adds horizontal scrolling if needed
              whiteSpace: 'pre-wrap', // Wraps lines
              wordWrap: 'break-word', // Breaks long words
            }}
          >
            <CopyBlock
              key={`codeblock-${lineIndexRef.current}`}
              text={buffer.trim()}
              language={currentLanguage}
              showLineNumbers
              theme={dracula}
            />
          </div>
        );
        isInCodeBlock = false;
        buffer = '';
        currentLanguage = 'plaintext';
        codeBlockStartIndex = -1;
      } else if (trimmed === '<code-explanation>') {
        isInExplanation = true;
        buffer = '';
      } else if (trimmed === '</code-explanation>') {
        currentNodes.push(<p key={`explanation-${lineIndexRef.current}`}><em>{buffer.trim()}</em></p>);
        buffer = '';
        isInExplanation = false;
      } else if (isInCodeBlock || isInExplanation) {
        buffer += line + '\n';
      } else {
        currentNodes.push(
          <ReactMarkdown
            key={`line-${lineIndexRef.current}`}
            remarkPlugins={[remarkGfm]}
          >
            {line}
          </ReactMarkdown>
        );
      }

      setParsedNodes([...currentNodes]);
      lineIndexRef.current += 1;
    }, 30);

    return () => {
      clearInterval(interval);
    };
  }, [content]);

  return <div>{parsedNodes}</div>;
};